import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../../services/api';

const NAV = [
  { section: 'Principal' },
  { path: '/',              icon: 'ti-layout-dashboard', label: 'Dashboard',       roles: ['*'] },
  { section: 'Clínica' },
  { path: '/pacientes',     icon: 'ti-users',            label: 'Pacientes',       roles: ['*'] },
  { path: '/turnos',        icon: 'ti-calendar-event',   label: 'Turnos',          roles: ['MEDICO','SECRETARIA','DBA','ADMINISTRADOR'] },
  { path: '/historia',      icon: 'ti-report-medical',   label: 'Historia clínica',roles: ['MEDICO','DBA','ADMINISTRADOR'] },
  { section: 'Servicios' },
  { path: '/laboratorio',   icon: 'ti-microscope',       label: 'Laboratorio',     roles: ['LABORATORIO','DBA','ADMINISTRADOR'] },
  { path: '/facturacion',   icon: 'ti-receipt',          label: 'Facturación',     roles: ['SECRETARIA','LABORATORIO','DBA','ADMINISTRADOR'] },
  { section: 'Sistema' },
  { path: '/reportes',      icon: 'ti-chart-bar',        label: 'Reportes',        roles: ['DBA','ADMINISTRADOR','SECRETARIA'] },
  { path: '/configuracion', icon: 'ti-settings',         label: 'Configuración',   roles: ['DBA','ADMINISTRADOR','SECRETARIA'] },
];

const BOTTOM_NAV = [
  { path: '/',             icon: 'ti-layout-dashboard', label: 'Inicio' },
  { path: '/pacientes',    icon: 'ti-users',            label: 'Pacientes' },
  { path: '/turnos',       icon: 'ti-calendar-event',   label: 'Turnos' },
  { path: '/historia',     icon: 'ti-report-medical',   label: 'Historia' },
  { path: '/configuracion',icon: 'ti-settings',         label: 'Más' },
];

const TITLES = {
  '/': 'Dashboard', '/pacientes': 'Pacientes', '/turnos': 'Turnos',
  '/historia': 'Historia clínica', '/laboratorio': 'Laboratorio',
  '/facturacion': 'Facturación', '/configuracion': 'Configuración',
  '/reportes': 'Reportes',
};

// Roles que ven la campanita de cargos pendientes — los mismos que
// ya tienen acceso a la pantalla de Facturación
const ROLES_FACTURACION = ['SECRETARIA', 'LABORATORIO', 'DBA', 'ADMINISTRADOR'];

// Determina el tipo de usuario efectivo para filtrar el menú
function getTipoEfectivo(usuario) {
  if (!usuario) return null;
  if (usuario.esDBA) return 'DBA';
  const tipo = (usuario.tipo_usuario || '').toUpperCase().trim();
  return tipo || 'MEDICO'; // fallback a MEDICO si no tiene tipo
}

// Verifica si el usuario puede ver un ítem del menú
function puedeVerItem(item, usuario) {
  if (!item.roles) return true; // secciones sin roles (headers)
  if (item.roles.includes('*')) return true;
  const tipo = getTipoEfectivo(usuario);
  return item.roles.includes(tipo);
}

// Componente que protege rutas en el backend también
function RutaProtegida({ path, usuario, children }) {
  const item = NAV.find(n => n.path === path);
  if (!item || puedeVerItem(item, usuario)) return children;
  return <Navigate to="/" replace />;
}

export default function AppLayout() {
  const { usuario, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const tipoEfectivo = getTipoEfectivo(usuario);
  const verCampanita = ROLES_FACTURACION.includes(tipoEfectivo);

  const [pendientes, setPendientes]   = useState(0);
  const [listaPend, setListaPend]     = useState([]);   // pacientes con cargos pendientes
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [toast, setToast]             = useState(null); // { codPaciente, paciente, descripcion, valor } | null
  const conteoAnteriorRef = useRef(null); // null = todavía no se cargó la primera vez
  const bellRef = useRef(null);

  useEffect(() => {
    if (!verCampanita) return;

    const consultar = async () => {
      try {
        const { data } = await api.get('/facturacion/pendientes-count');
        setPendientes(data.count || 0);
        setListaPend(data.lista || []);

        // Solo avisa con toast si el conteo SUBIÓ respecto a la consulta
        // anterior — la primera carga nunca dispara el toast (evita
        // notificar de golpe todo lo que ya estaba pendiente antes).
        if (conteoAnteriorRef.current !== null && data.count > conteoAnteriorRef.current && data.ultimo) {
          setToast({
            codPaciente: data.ultimo.COD_PACIENTE,
            paciente: data.ultimo.PACIENTE?.trim() || 'Paciente',
            descripcion: data.ultimo.DESCRIPCION || 'Cargo pendiente',
            valor: data.ultimo.VALOR,
          });
          setTimeout(() => setToast(null), 6000);
        }
        conteoAnteriorRef.current = data.count;
      } catch (e) {
        // silencioso — no interrumpe la navegación si falla el ping
      }
    };

    consultar();
    const intervalo = setInterval(consultar, 30000); // cada 30s
    return () => clearInterval(intervalo);
  }, [verCampanita]);

  // Cierra el panel desplegable al hacer clic afuera
  useEffect(() => {
    if (!panelAbierto) return;
    const onClickFuera = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setPanelAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [panelAbierto]);

  const irAFacturarPaciente = (codPaciente) => {
    setPanelAbierto(false);
    setToast(null);
    navigate(`/facturacion?paciente=${codPaciente}`);
  };

  const initials = usuario?.nombres
    ? usuario.nombres.split(' ').slice(0,2).map(w=>w[0]).join('')
    : '??';

  const titulo = Object.entries(TITLES)
    .find(([p]) => location.pathname === p || (p !== '/' && location.pathname.startsWith(p)))?.[1] || '';

  const hoy = format(new Date(), "EEE d MMM yyyy", { locale: es });

  const closeSidebar = () => setSidebarOpen(false);
  const navTo = (path) => { navigate(path); closeSidebar(); };

  return (
    <div className="app-shell">
      {/* Overlay para tablet/mobile */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={closeSidebar}
      />

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo" style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src="/logo-icono.png" alt="Logo" style={{ width:38, height:38, objectFit:'contain', flexShrink:0 }} />
          <div>
            <div className="sidebar-logo-title" style={{ fontSize:14, lineHeight:1.2 }}>Dra. Sandra Culqui</div>
            <div className="sidebar-logo-sub" style={{ fontSize:10 }}>Medicina Interna y Obesidad</div>
          </div>
        </div>
        <div className="sidebar-user">
          <div className="sidebar-avatar">{initials}</div>
          <div>
            <div className="sidebar-user-name">{usuario?.nombres?.split(' ').slice(0,2).join(' ')}</div>
            <div className="sidebar-user-role">{usuario?.usuario}</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {(() => {
            // Filtrar ítems por rol, ocultando también secciones que queden vacías
            const visibles = [];
            let seccionPendiente = null;
            for (const item of NAV) {
              if (item.section) {
                seccionPendiente = item;
              } else if (puedeVerItem(item, usuario)) {
                if (seccionPendiente) { visibles.push(seccionPendiente); seccionPendiente = null; }
                visibles.push(item);
              }
            }
            return visibles.map((item, i) =>
              item.section ? (
                <div key={i} className="nav-section-label">{item.section}</div>
              ) : (
                <div key={item.path}
                  className={`nav-item ${location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)) ? 'active' : ''}`}
                  onClick={() => navTo(item.path)}>
                  <i className={`ti ${item.icon} nav-icon`} aria-hidden="true"></i>
                  {item.label}
                </div>
              )
            );
          })()}
        </nav>
        <div className="sidebar-footer">
          <button className="btn-logout" onClick={logout}>Cerrar sesión</button>
        </div>
      </aside>

      {/* ── Área principal ── */}
      <div className="main-area">
        <header className="topbar">
          {/* Botón hamburguesa (visible solo en tablet/mobile via CSS) */}
          <button
            className="topbar-hamburger"
            onClick={() => setSidebarOpen(s => !s)}
            aria-label="Abrir menú"
            style={{ display: 'none' }}
            ref={el => {
              if (el) {
                const mq = window.matchMedia('(max-width: 900px)');
                el.style.display = mq.matches ? 'flex' : 'none';
                mq.onchange = e => { el.style.display = e.matches ? 'flex' : 'none'; };
              }
            }}>
            <span /><span /><span />
          </button>
          <div className="topbar-title">{titulo}</div>
          <div className="topbar-right">
            {verCampanita && (
              <div className="topbar-bell-wrap" ref={bellRef}>
                <button
                  className="topbar-bell"
                  onClick={() => setPanelAbierto((v) => !v)}
                  aria-label="Cargos pendientes de facturar"
                  title="Cargos pendientes de facturar"
                >
                  <i className="ti ti-bell" aria-hidden="true"></i>
                  {pendientes > 0 && <span className="topbar-bell-badge">{pendientes}</span>}
                </button>

                {panelAbierto && (
                  <div className="bell-panel">
                    <div className="bell-panel-hdr">
                      Pendientes de facturar
                      <span className="bell-panel-hdr-count">{pendientes}</span>
                    </div>
                    {listaPend.length === 0 && (
                      <div className="bell-panel-empty">No hay cargos pendientes.</div>
                    )}
                    {listaPend.map((p) => (
                      <div
                        key={p.COD_PACIENTE}
                        className="bell-panel-item"
                        onClick={() => irAFacturarPaciente(p.COD_PACIENTE)}
                      >
                        <div>
                          <div className="bell-panel-item-nombre">{p.PACIENTE?.trim() || 'Paciente'}</div>
                          <div className="bell-panel-item-sub">{p.cargos} cargo{p.cargos !== 1 ? 's' : ''}</div>
                        </div>
                        <div className="bell-panel-item-total">${Number(p.total_pendiente).toFixed(2)}</div>
                      </div>
                    ))}
                    {pendientes > listaPend.length && (
                      <div className="bell-panel-mas" onClick={() => { setPanelAbierto(false); navigate('/facturacion'); }}>
                        Ver todos ({pendientes})
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <span className="topbar-date">{hoy}</span>
          </div>
        </header>

        <main className="page-content fade-up">
          <Outlet />
        </main>

        {/* ── Barra inferior mobile ── */}
        <nav className="bottom-nav" style={{ display: 'none' }}>
          {BOTTOM_NAV.map(item => (
            <div key={item.path}
              className={`bottom-nav-item ${location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)) ? 'active' : ''}`}
              onClick={() => navigate(item.path)}>
              <i className={`ti ${item.icon} bottom-nav-icon`} aria-hidden="true"></i>
              {item.label}
            </div>
          ))}
        </nav>
      </div>

      {/* ── Toast de cargo nuevo pendiente ── */}
      {toast && (
        <div className="pendiente-toast" onClick={() => irAFacturarPaciente(toast.codPaciente)}>
          <div className="pendiente-toast-icon">💵</div>
          <div>
            <div className="pendiente-toast-title">Nuevo cargo pendiente</div>
            <div className="pendiente-toast-body">
              {toast.paciente} · {toast.descripcion} · ${Number(toast.valor).toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
